import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthComponent';
import { Button, DatePicker, Popover, Select } from '@shopify/polaris';
import { CalendarIcon } from '@shopify/polaris-icons';
import { TZ, etParts, etToISO, timeOptions } from './bopisTime';
import { badgeForOpponent, GENERIC_CREST } from './gothamTeamBadges';

// BOPIS match-day store scheduler, shared by Gotham FC and Washington Spirit.
// The backend opens/closes each club's stadium-pickup store on its own for
// every home game (<club>_bopis_scheduler.py, cron every 15 min). This page
// shows the store's state, the next game's ON/OFF window (editable), the three
// steps with manual "now" buttons, and the activity log.
// Design: "Match-Day Store Scheduler" canvas (claude.ai artifact).
// `team` (GOTHAM / SPIRIT below) holds everything club-specific; `themeClass`
// re-skins the page through the --gs-* tokens in theme/uh.css.

const API = ''; // same-origin
const POLL_MS = 60 * 1000;

export const GOTHAM = {
  api: '/api/gotham-bopis',
  eyebrow: 'Gotham BOPIS · Stadium pickup',
  stadium: 'Sports Illustrated Stadium, Harrison NJ',
  homeCrest: '/images/team-badges/gotham-fc.svg',
  homeName: 'Gotham FC',
  metafieldNs: 'gotham_bopis',
  themeClass: '',
  pickupReady: true, // BOPIS orders card (gotham_bopis_pickup.py)
  pickupVenue: 'SI Stadium',
  step4Key: 'uh.gothamBopis.step4Open',
  fontsHref: 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap',
};

// Colors and type from the Spirit Shopify store's published theme.
export const SPIRIT = {
  api: '/api/spirit-bopis',
  eyebrow: 'Spirit BOPIS · Stadium pickup',
  stadium: 'Audi Field, Washington DC',
  homeCrest: '/images/team-badges/washington-spirit.svg',
  homeName: 'Washington Spirit',
  metafieldNs: 'spirit_bopis',
  themeClass: 'spirit',
  pickupReady: true, // BOPIS orders card (spirit_bopis_pickup.py)
  pickupVenue: 'Audi Field',
  step4Key: 'uh.spiritBopis.step4Open',
  fontsHref: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:opsz,wght@6..12,400;6..12,600;6..12,700;6..12,800&family=IBM+Plex+Mono:wght@500&display=swap',
};

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function errorText(r, path) {
  try {
    const body = await r.json();
    if (body.detail) return typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    if (body.error) return body.error;
  } catch (e) { /* not JSON */ }
  return `${path} → ${r.status}`;
}

async function getJSON(path) {
  const r = await fetch(`${API}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(await errorText(r, `GET ${path}`));
  return r.json();
}

async function sendJSON(method, path, body) {
  const r = await fetch(`${API}${path}`, {
    method,
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await errorText(r, `${method} ${path}`));
  return r.json();
}

async function* streamSSE(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify(body),
  });
  if (!r.ok || !r.body) throw new Error(`POST ${path} → ${r.status}`);

  const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let sep;
    while ((sep = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = 'message', data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      yield { event, data: data ? JSON.parse(data) : {} };
    }
  }
}

// --- Time helpers (everything shown in ET) ----------------------------------

const fmtParts = (iso, opts) => new Date(iso).toLocaleString('en-US', { timeZone: TZ, ...opts });
const fmtDate = (iso) => (iso ? fmtParts(iso, { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
const fmtTime = (iso) => (iso ? `${fmtParts(iso, { hour: 'numeric', minute: '2-digit' })} ET` : '—');
const fmtDT = (iso) => (iso ? `${fmtDate(iso)}, ${fmtTime(iso)}` : '—');
const fmtStamp = (iso) => (iso ? `${fmtParts(iso, { month: 'short', day: 'numeric' })} · ${fmtTime(iso)}` : '');

// "YYYY-MM-DD" game date as a local calendar date (no UTC off-by-one).
function parseGameDate(s) {
  const [Y, M, D] = s.split('-').map(Number);
  return new Date(Y, M - 1, D);
}

function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: TZ }); // YYYY-MM-DD
}

function fmtKickoff(kickoff) {
  if (!kickoff) return 'kickoff TBA';
  return new Date(`1970-01-01T${kickoff}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) + ' ET';
}

function windowLength(openIso, closeIso, kickIso) {
  const hrs = Math.round((new Date(closeIso) - new Date(openIso)) / 3600000);
  let s = `Live for ${Math.floor(hrs / 24)}d ${hrs % 24}h`;
  if (kickIso) {
    const before = Math.round((new Date(kickIso) - new Date(closeIso)) / 3600000);
    if (before > 0) s += ` · off ${before}h before kickoff`;
  }
  return s;
}

// Kickoff as an ISO instant, when the schedule has a time.
function kickoffISO(game) {
  if (!game?.kickoff_time) return null;
  return etToISO(game.game_date, game.kickoff_time.slice(0, 5));
}

// --- Small pieces -------------------------------------------------------------

function Crest({ src, alt }) {
  const [broken, setBroken] = useState(false);
  return <img className="gs-crest" src={broken ? GENERIC_CREST : src} alt={alt} onError={() => setBroken(true)} />;
}

function Pill({ tone, children }) {
  return <span className={`gs-pill ${tone || ''}`}>{children}</span>;
}

const EDGE_WORD = { open: 'on', close: 'off' };

// Status pill + "when" text for one window edge.
function edgeStatus(w, edge, paused, maxAttempts) {
  if (!w) return { label: 'No game', tone: 'muted', when: '—' };
  const st = w[`${edge}_status`];
  const at = w[`${edge}_at`];
  const tried = w[`${edge}_last_try_at`];
  switch (st) {
    case 'done': {
      const by = w[`${edge}_by`];
      return by
        ? { label: 'Done manually', tone: 'done', when: `By ${by} · ${fmtDT(tried || at)}` }
        : { label: 'Completed', tone: 'done', when: `Ran ${fmtDT(tried || at)}` };
    }
    case 'retrying': return { label: `Retrying · try ${w[`${edge}_attempts`] + 1} of ${maxAttempts}`, tone: 'warn', when: `Last try ${fmtDT(tried)}` };
    case 'failed': return { label: `Failed · ${w[`${edge}_attempts`]} of ${maxAttempts} tries`, tone: 'fail', when: `Last try ${fmtDT(tried)}` };
    case 'missed': return { label: 'Missed', tone: 'muted', when: `${fmtDT(at)} passed before it could run` };
    case 'skipped': return { label: `Skipped · already ${EDGE_WORD[edge]}`, tone: 'muted', when: fmtDT(at) };
    default:
      return paused
        ? { label: 'Paused', tone: 'muted', when: `${fmtDT(at)} (won’t run)` }
        : { label: 'Scheduled', tone: 'sched', when: fmtDT(at) };
  }
}

// --- Store window (ON / OFF edges + timeline) -----------------------------------

// Polaris calendar in a popover; value is an ET "YYYY-MM-DD".
function EtDatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseGameDate(value) : null;
  const [{ month, year }, setMonthYear] = useState(() => {
    const d = selected || new Date();
    return { month: d.getMonth(), year: d.getFullYear() };
  });
  useEffect(() => { if (selected) setMonthYear({ month: selected.getMonth(), year: selected.getFullYear() }); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = selected
    ? selected.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : 'Pick a date';
  const activator = (
    <Button icon={CalendarIcon} disclosure onClick={() => setOpen((o) => !o)}>{label}</Button>
  );
  return (
    <Popover active={open} activator={activator} onClose={() => setOpen(false)} preferredAlignment="left">
      <div style={{ padding: 16 }}>
        <DatePicker
          month={month}
          year={year}
          onMonthChange={(m, y) => setMonthYear({ month: m, year: y })}
          selected={selected ? { start: selected, end: selected } : undefined}
          onChange={({ start }) => {
            const ymd = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
            onChange(ymd);
            setOpen(false);
          }}
        />
      </div>
    </Popover>
  );
}

function EdgeBox({ w, edge, editing, onEdit, onCancel, onSave, onReset, busy, error }) {
  const on = edge === 'open';
  const at = w[`${edge}_at`];
  const custom = w[`${edge}_custom`];
  const st = w[`${edge}_status`];
  const [draft, setDraft] = useState({ date: '', time: '' });
  useEffect(() => { if (editing) setDraft(etParts(at)); }, [editing, at]);

  const tag = st === 'pending' ? (custom ? ['Custom time', 'warn'] : ['By rule', 'muted'])
    : st === 'done' ? [w[`${edge}_by`] ? 'Done manually' : 'Done', 'done']
    : st === 'failed' ? ['Failed', 'fail']
    : st === 'retrying' ? ['Retrying', 'warn']
    : [st === 'missed' ? 'Missed' : 'Skipped', 'muted'];
  const rule = custom
    ? `Custom · rule would be ${fmtDT(w[`rule_${edge}_at`])}`
    : on ? 'Rule: Monday 10:00 AM of game week' : 'Rule: Friday noon, or noon the day before a Friday game';
  return (
    <div className={`gs-edge ${on ? 'on' : 'off'}${custom ? ' custom' : ''}`}>
      <div className="gs-edge-top">
        <span className="gs-edge-badge">{on ? 'Turns ON' : 'Turns OFF'}</span>
        <Pill tone={tag[1]}>{tag[0]}</Pill>
      </div>
      <div className="gs-edge-date">{fmtDate(at)}</div>
      <div className="gs-edge-time">{fmtTime(at)}</div>
      <div className="gs-edge-rule">
        {st === 'done' && w[`${edge}_by`]
          ? `Turned ${EDGE_WORD[edge]} by ${w[`${edge}_by`]} · ${fmtDT(w[`${edge}_last_try_at`])}`
          : rule}
      </div>
      {editing ? (
        <div className="gs-edit">
          <span className="gs-edit-label">New turn-{EDGE_WORD[edge]} time (ET)</span>
          <div className="gs-edit-pick">
            <EtDatePicker value={draft.date} onChange={(date) => setDraft((d) => ({ ...d, date }))} />
            <Select
              label="Time"
              labelHidden
              options={timeOptions(draft.time)}
              value={draft.time}
              onChange={(time) => setDraft((d) => ({ ...d, time }))}
            />
          </div>
          {error && <div role="alert" className="gs-err">{error}</div>}
          <div className="gs-row">
            <button type="button" className="gs-btn save" disabled={busy} onClick={() => onSave(draft)}>Save time</button>
            <button type="button" className="gs-btn ghost" disabled={busy} onClick={onCancel}>Cancel</button>
          </div>
        </div>
      ) : st === 'pending' && (
        <div className="gs-row">
          <button type="button" className="gs-btn outline" onClick={onEdit}>Change turn-{EDGE_WORD[edge]} time</button>
          {custom && <button type="button" className="gs-btn link" disabled={busy} onClick={onReset}>Reset to rule</button>}
        </div>
      )}
    </div>
  );
}

function Timeline({ openIso, closeIso, kickIso }) {
  const a = new Date(openIso).getTime();
  const b = new Date(closeIso).getTime();
  const now = Date.now();
  const k = kickIso ? new Date(kickIso).getTime() : null;
  const pts = [a, b, now, ...(k ? [k] : [])];
  const pad = (b - a) * 0.06;
  const lo = Math.min(...pts) - pad;
  const hi = Math.max(...pts) + pad;
  const pct = (x) => `${Math.max(0, Math.min(100, ((x - lo) / (hi - lo)) * 100))}%`;
  const left = (x) => ({ left: pct(x) });
  return (
    <div className="gs-timeline" aria-hidden="true">
      <div className="gs-track">
        <div className="gs-bar" style={{ left: pct(a), width: `calc(${pct(b)} - ${pct(a)})` }} />
        <div className="gs-now" style={left(now)} title="Now" />
        {k && <div className="gs-kick" style={left(k)} title="Kickoff" />}
      </div>
      <div className="gs-tl-labels">
        <span className="on" style={left(a)}>ON</span>
        <span className="now" style={left(now)}>NOW</span>
        <span style={left(b)}>OFF</span>
        {k && <span className="kick" style={left(k)}>KICKOFF</span>}
      </div>
    </div>
  );
}

// --- Steps ---------------------------------------------------------------------

const STEP_ICON = {
  pending: ['○', 'muted'], running: ['⟳', 'run'], ok: ['✓', 'ok'], fail: ['✗', 'fail'], skipped: ['—', 'muted'],
};

function RunSteps({ heading, steps }) {
  return (
    <div className="gs-run">
      {heading && <div className="gs-run-h">{heading}</div>}
      <ul>
        {steps.map((s) => {
          const [icon, tone] = STEP_ICON[s.status] || STEP_ICON.pending;
          return (
            <li key={s.name}>
              {s.status === 'upcoming'
                ? <span className="gs-run-i"><span className="gs-run-box" aria-label="Not run yet" /></span>
                : <span className={`gs-run-i ${tone}`}>{icon}</span>}
              <span className={`gs-run-l ${tone}`}>
                {s.label || s.name}
                {s.error ? <em> · {s.error}</em> : s.detail && s.status === 'ok' ? <em> · {s.detail}</em> : null}
              </span>
              {s.shopify_admin_url && s.status !== 'pending' && (
                <a href={s.shopify_admin_url} target="_blank" rel="noreferrer">View in Shopify</a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Steps from the latest history row for an action.
function stepsFromHistory(row) {
  return (row.shopify_results || []).map((s) => ({
    name: s.name, label: s.name, status: s.ok ? 'ok' : 'fail',
    detail: s.detail, error: s.error, shopify_admin_url: s.shopify_admin_url,
  }));
}

// `upcoming` lists the next run's steps unchecked; `compactLast` folds the last run
// into a one-line disclosure under the card instead of the side button.
function StepCard({ n, title, desc, rule, status, when, custom, runLabel, runDisabled, onRun, runView, upcoming, compactLast }) {
  const [open, setOpen] = useState(false);
  const live = runView?.live;
  const showRun = runView && (open || live) && !(compactLast && !live);
  return (
    <li className={`gs-step ${status.tone === 'fail' ? 'fail' : custom ? 'custom' : ''}`}>
      <div className="gs-step-main">
        <div className={`gs-num ${status.tone}`}>{String(n).padStart(2, '0')}</div>
        <div className="gs-step-body">
          <div className="gs-step-h">
            <h3>{title}</h3>
            <Pill tone={status.tone}>{status.label}</Pill>
          </div>
          <p>{desc}</p>
          <div className="gs-step-meta">
            <span>{custom ? 'Rule · custom time' : `Rule · ${rule}`}</span>
            <span className={status.tone === 'fail' ? 'fail' : custom ? 'custom' : ''}>{when}</span>
          </div>
        </div>
        <div className="gs-step-acts">
          <button type="button" className="gs-btn primary" disabled={runDisabled} onClick={onRun}>{runLabel}</button>
          {runView && !live && !compactLast && (
            <button type="button" className="gs-btn ghost sm" aria-expanded={open} onClick={() => setOpen(!open)}>
              {open ? 'Hide Shopify steps' : 'Show Shopify steps'}
            </button>
          )}
        </div>
      </div>
      {showRun && <RunSteps heading={runView.heading} steps={runView.steps} />}
      {upcoming && !live && <RunSteps heading={upcoming.heading} steps={upcoming.steps} />}
      {compactLast && runView && !live && (
        <div className="gs-run gs-run-last">
          <button type="button" className="gs-run-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
            <span className="chev">{open ? '▾' : '▸'}</span>
            <span>{runView.heading}</span>
          </button>
          {open && <RunSteps heading={null} steps={runView.steps} />}
        </div>
      )}
    </li>
  );
}

// --- Live Shopify state -----------------------------------------------------------

function LiveState({ snapshot, loading, error, checkedAt, onRefresh, metafieldNs }) {
  const [open, setOpen] = useState(false);
  const toggle = () => {
    if (!open && !snapshot && !loading) onRefresh();
    setOpen(!open);
  };
  return (
    <div className="gs-panel gs-live">
      <div className="gs-live-h">
        <button type="button" className="gs-disclose" aria-expanded={open} onClick={toggle}>
          <span className="chev">{open ? '▾' : '▸'}</span>
          <span className="gs-h2">Live Shopify state</span>
        </button>
        <div className="gs-row">
          {checkedAt && <span className="gs-sub">Checked {fmtStamp(checkedAt)}</span>}
          <button type="button" className="gs-btn ghost sm" disabled={loading} onClick={() => { setOpen(true); onRefresh(); }}>
            {loading ? 'Checking…' : 'Refresh'}
          </button>
        </div>
      </div>
      {open && error && <div role="alert" className="gs-err">{error}</div>}
      {open && snapshot && (
        <div className="gs-live-grid">
          <div>
            <div className="gs-cap">Location</div>
            <div className="strong">{snapshot.location.name}</div>
            <div>Fulfills online orders: <b>{snapshot.location.fulfills_online_orders ? 'YES' : 'no'}</b></div>
            <div>Local pickup: <b>{snapshot.location.local_pickup_enabled ? 'YES' : 'no'}</b></div>
          </div>
          <div>
            <div className="gs-cap">Main menu</div>
            {snapshot.menu.items.map((it, i) => (
              <div key={i} className={i === 0 && snapshot.menu.has_stadium_collection_at_position_0 ? 'hi' : 'dim'}>
                {i + 1}. {it.title}
              </div>
            ))}
          </div>
          <div className="mono">
            <div className="gs-cap">{metafieldNs} metafields</div>
            {Object.entries(snapshot.collection.metafields).map(([k, v]) => (
              <div key={k}><span className="dim">{k}:</span> {v === null ? '(unset)' : String(v)}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Step 4, BOPIS orders: after turn-off, move them to the stadium (`venue`), then mark them Ready for pickup
// (B runs on its own 2 hours before kickoff unless someone aborts it)

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const fmtMoney = (n, currency) => new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(n || 0);

function PickupResult({ ok, title, failed, children }) {
  return (
    <div role="status" className={`gs-pick-result ${ok ? 'ok' : 'fail'}`}>
      <div className="strong">{title}</div>
      {failed && failed.length > 0 && (
        <ul>
          {failed.map((f) => (
            <li key={f.name}><b>{f.name}</b> · {f.error || 'unknown error'}</li>
          ))}
        </ul>
      )}
      {children}
    </div>
  );
}

// One of step 4's two actions, shown under the step's "BOPIS steps" toggle:
// letter, title, status pill, rule · when line, what it does, its button.
function SubStep({ letter, title, status, rule, when, action, children }) {
  return (
    <div className="gs-pick-step">
      <div className={`gs-num ${status.tone}`}>{letter}</div>
      <div className="gs-step-body">
        <div className="gs-step-h">
          <h4>{title}</h4>
          <Pill tone={status.tone}>{status.label}</Pill>
        </div>
        <div className="gs-step-meta">
          <span>Rule · {rule}</span>
          <span>{when}</span>
        </div>
        {children}
      </div>
      {action}
    </div>
  );
}

const readStep4Open = (key) => { try { return localStorage.getItem(key) === '1'; } catch { return false; } };

function PickupOrders({ api, w, n, venue, storageKey }) {
  const { nameOf } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null); // 'move' | 'mark' | 'auto'
  const [moveResult, setMoveResult] = useState(null); // { moved, failed, stock }
  const [result, setResult] = useState(null); // { marked, already_ready, failed }
  const [open, setOpen] = useState(() => readStep4Open(storageKey)); // closed until opened; remembered per browser

  const toggle = () => {
    setOpen(!open);
    try { localStorage.setItem(storageKey, open ? '0' : '1'); } catch { /* then it just isn't remembered */ }
  };

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setInfo(await getJSON(`${api}/windows/${w.id}/pickup-orders`)); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [api, w.id]);

  useEffect(() => { setInfo(null); setResult(null); setMoveResult(null); load(); }, [load]);

  const moveAll = async () => {
    if (!window.confirm(`Move ${plural(info.to_move, 'BOPIS order')} to ${venue}?\n\n`
      + `Items ${venue} doesn't stock yet are added there, and the moved units' stock moves `
      + `from Eatontown to ${venue} in Shopify. Customers aren't emailed.`)) return;
    setBusy('move'); setErr(null); setMoveResult(null); setResult(null);
    try {
      const r = await sendJSON('POST', `${api}/windows/${w.id}/pickup-move`, {});
      setInfo(r);
      setMoveResult({ moved: r.moved, failed: r.failed, stock: r.stock });
    } catch (e) { setErr(`Moving failed: ${e.message}`); }
    finally { setBusy(null); }
  };

  const markAll = async () => {
    if (!window.confirm(`Mark ${plural(info.to_mark, 'BOPIS order')} ready for pickup?\n\n`
      + 'Shopify emails each customer that their order is ready to collect.')) return;
    setBusy('mark'); setErr(null); setResult(null);
    try {
      const r = await sendJSON('POST', `${api}/windows/${w.id}/pickup-ready`, {});
      setInfo(r);
      setResult({ marked: r.marked, already_ready: r.already_ready, failed: r.failed });
    } catch (e) { setErr(`Marking failed: ${e.message}`); }
    finally { setBusy(null); }
  };

  // Abort B's automatic run (aborted=true), or turn it back on.
  const setAborted = async (aborted) => {
    if (aborted && !window.confirm(`Abort the automatic mark ready for vs ${w.opponent}?\n\n`
      + `Nothing is sent at ${fmtTime(info.ready_auto.at)}. The orders stay as they are until someone presses `
      + 'Mark all ready for pickup.')) return;
    setBusy('auto'); setErr(null); setResult(null);
    try { setInfo(await sendJSON('POST', `${api}/windows/${w.id}/pickup-ready-auto`, { aborted })); }
    catch (e) { setErr(`${aborted ? 'Aborting' : 'Turning the automation back on'} failed: ${e.message}`); }
    finally { setBusy(null); }
  };

  const who = (actor) => (actor === 'auto' ? 'auto' : nameOf(actor));
  const others = info ? [
    info.already && `${info.already} already ready`,
    info.not_open && `${info.not_open} picked up or not for pickup`,
    info.cancelled && `${info.cancelled} cancelled`,
  ].filter(Boolean) : [];
  const off = !!info && info.store_off;
  const from = info && info.move_from.length ? info.move_from.join(', ') : 'another location';
  const ml = info && info.move_last;
  const kl = info && info.mark_last;
  const turnOff = fmtDT(w.close_at);

  // A · Move to the stadium
  const aDone = off && info.to_move === 0;
  const aStatus = !info ? { label: '—', tone: '' }
    : !off ? { label: 'Scheduled', tone: 'sched' }
      : aDone ? { label: 'Completed', tone: 'done' }
        : { label: 'To do', tone: 'warn' };
  const aWhen = !info ? '—'
    : !off ? `Expected ${turnOff} · with turn-off`
      : ml ? `Ran ${fmtDT(ml.at)} · ${who(ml.actor)}`
        + (ml.moved != null ? ` · ${ml.moved} moved` : '') + (ml.units ? `, ${plural(ml.units, 'unit')}` : '')
        : aDone ? 'Nothing needed moving' : 'Not run yet';

  // B · Mark all ready for pickup
  const bDone = !!info && info.mark_unlocked && info.to_mark === 0 && info.already > 0;
  const bStatus = !info ? { label: '—', tone: '' }
    : !info.mark_unlocked ? { label: 'Waiting', tone: '' }
      : info.to_mark > 0 ? { label: 'To do', tone: 'warn' }
        : bDone ? { label: 'Completed', tone: 'done' } : { label: 'Nothing to mark', tone: '' };
  const bWhen = !info ? '—'
    : !off ? `Expected ${turnOff} · with turn-off`
      : !info.mark_unlocked ? 'Unlocks after A'
        : kl && info.to_mark === 0 ? `Ran ${fmtDT(kl.at)} · ${who(kl.actor)} · ${kl.orders} marked`
          : kl ? `Last ran ${fmtDT(kl.at)} · ${who(kl.actor)}`
            : `Unlocked ${fmtDT(ml ? ml.at : w.close_at)}`;

  // B's automatic run, 2 hours before kickoff. `armed`: it will still run, so Abort shows
  // instead of the button. Once it's aborted, failed or missed, the button is back.
  const ra = info && info.ready_auto;
  const armed = !!ra && !!ra.at && ['pending', 'retrying'].includes(ra.status) && !bDone;
  const kickoffPassed = !!ra && !!ra.kickoff_at && new Date(ra.kickoff_at) <= new Date();
  let bAuto = null; // { status, when, text } overriding the manual ones
  if (ra && !bDone) {
    const runs = ra.at && `${fmtDT(ra.at)} · 2 h before ${fmtTime(ra.kickoff_at)} kickoff`;
    if (armed && ra.paused) {
      bAuto = { status: { label: 'Paused', tone: '' }, when: `${fmtDT(ra.at)} (won’t run while paused)`,
        text: 'Automation is paused at the top of the page, so nothing is marked on its own. Abort to mark them yourself.' };
    } else if (armed && ra.status === 'retrying') {
      bAuto = { status: { label: `Retrying · try ${ra.attempts + 1} of ${ra.max_attempts}`, tone: 'warn' },
        when: `Last try ${fmtDT(ra.last_try_at)}`,
        text: <>The automatic run failed ({ra.last_error || 'unknown error'}). It tries again within 15 min. Abort to mark them yourself.</> };
    } else if (armed) {
      bAuto = { status: { label: 'Scheduled', tone: 'sched' }, when: `Runs ${runs}`,
        text: <><b>{plural(info.to_mark || info.total, 'order')}</b> {info.to_mark === 1 ? 'is' : 'are'} marked ready on their own at {fmtTime(ra.at)}.
          {' '}Shopify emails each customer. Abort to stop it and mark them yourself.</> };
    } else if (ra.status === 'aborted') {
      bAuto = { status: { label: 'Aborted', tone: 'fail' }, when: `Aborted by ${who(ra.by)} · mark them yourself`,
        text: info.to_mark > 0
          ? <><b>{info.to_mark} to mark ready.</b> The automatic run was aborted. Press the button to mark every order ready. Shopify emails each customer.</>
          : 'The automatic run was aborted. Nothing to mark yet.' };
    } else if (ra.status === 'failed') {
      bAuto = { status: { label: `Failed · ${ra.attempts} of ${ra.max_attempts} tries`, tone: 'fail' },
        when: `Last try ${fmtDT(ra.last_try_at)}`,
        text: <>The automatic run failed: {ra.last_error || 'unknown error'}. Fix them in Shopify, then press the button to retry.</> };
    } else if (ra.status === 'missed') {
      bAuto = { status: { label: 'Missed', tone: '' }, when: 'Kickoff passed before it could run',
        text: info.to_mark > 0 ? <><b>{info.to_mark} to mark ready.</b> Press the button to mark them. Shopify emails each customer.</> : null };
    } else if (!ra.at && info.to_mark > 0 && info.mark_unlocked) {
      bAuto = { status: bStatus, when: 'Kickoff TBA · press it yourself',
        text: <><b>{info.to_mark} to mark ready.</b> The schedule has no kickoff time for this game, so nothing runs on its own. Shopify emails each customer.</> };
    }
  }
  if (ra && bDone && ra.status === 'done' && ra.by) {
    bAuto = { status: { label: 'Done manually', tone: 'done' }, when: bWhen, text: null };
  }
  const bRule = ra && ra.at ? '2 h before kickoff, on its own' : 'unlocks at turn-off, you press it';

  // Step 4 as a whole
  const allDone = aDone && bDone;
  const status = !info ? { label: loading ? 'Checking' : '—', tone: '' }
    : !off ? { label: 'After turn-off', tone: '' }
      : allDone ? { label: 'Completed', tone: 'done' }
        : ra && ra.status === 'failed' ? { label: 'Failed', tone: 'fail' }
          : info.to_move > 0 || (info.to_mark > 0 && !armed) ? { label: 'To do', tone: 'warn' }
            : armed ? { label: 'Scheduled', tone: 'sched' }
              : { label: 'Completed', tone: 'done' };
  const summary = !info ? 'Checking orders…'
    : !off ? <>{plural(info.total, 'order')} so far · runs when the store turns off</>
      : <>{plural(info.total, 'order')} this window
        {info.to_move > 0 && <> · <b>{info.to_move} to move</b></>}
        {info.to_mark > 0
          ? (armed ? <> · <b>marked ready on their own at {fmtTime(ra.at)}</b></> : <> · <b>{info.to_mark} to mark ready</b></>)
          : allDone ? ` · all at ${venue} and marked ready` : ''}
        {ra && ra.status === 'aborted' && info.to_mark > 0 && ' · automation aborted'}
      </>;
  const heading = !info ? 'BOPIS steps'
    : !off ? `BOPIS steps · A at turn-off, ${turnOff}${ra && ra.at ? ` · B ${fmtDT(ra.at)}` : ''}`
      : allDone ? 'BOPIS steps · all done'
        : `BOPIS steps · A ${aDone ? 'done' : `${info.to_move} to move`} · B ${
          armed ? `runs ${fmtDT(ra.at)}`
            : `${ra && ra.status === 'aborted' ? 'aborted, ' : ''}${
              info.to_mark > 0 ? `${info.to_mark} to mark ready` : bDone ? 'done' : 'nothing to mark'}`}`;

  return (
    <li className="gs-step gs-pick">
      <div className="gs-step-main">
        <div className={`gs-num ${status.tone}`}>{n}</div>
        <div className="gs-step-body">
          <div className="gs-step-h">
            <h3>BOPIS orders</h3>
            <Pill tone={status.tone}>{status.label}</Pill>
          </div>
          <p>{summary}</p>
          {info && <p className="gs-sub lg">{fmtMoney(info.gross_sales, info.currency)} gross sales{info.cancelled > 0 ? ' · cancelled orders not counted' : ''}</p>}
          <div className="gs-step-meta">
            <span>Rule · after turn-off</span>
            <span>{!info ? '—' : !off ? `Starts ${turnOff}` : ml ? `Moved ${fmtDT(ml.at)} · ${who(ml.actor)}` : `Store off · ${turnOff}`}</span>
          </div>
        </div>
        <div className="gs-step-acts">
          <button type="button" className="gs-btn ghost sm" disabled={loading || !!busy} onClick={load}>
            {loading ? 'Checking…' : 'Refresh'}
          </button>
        </div>
      </div>
      {err && <div role="alert" className="gs-err">{err}</div>}

      <div className="gs-run gs-run-last">
        <button type="button" className="gs-run-toggle" aria-expanded={open} onClick={toggle}>
          <span className="chev">{open ? '▾' : '▸'}</span>
          <span>{heading}</span>
        </button>
        {open && info && (
          <div className="gs-pick-steps">
            <div className="gs-sub lg">
              {plural(info.total, 'order')} placed {fmtDT(w.open_at)} – {fmtDT(w.close_at)}
              {others.length > 0 && ` · ${others.join(' · ')}`}
            </div>

            <SubStep letter="A" title={`Move to ${venue}`} status={aStatus} rule="at turn-off, on its own" when={aWhen}
              action={(
                <button type="button" className="gs-btn primary" onClick={moveAll}
                  disabled={!!busy || loading || !off || !info.can_mark || info.to_move === 0}>
                  {busy === 'move' ? 'Moving…' : info.to_move > 0 ? `Move ${info.to_move} to ${venue}` : 'Nothing to move'}
                </button>
              )}>
              <p>
                {!off
                  ? `Runs on its own when the store turns off and moves every order still at ${from}, with its stock.`
                    + (info.to_move > 0 ? ` ${plural(info.to_move, 'order')} there so far.` : '')
                  : info.to_move > 0
                    ? <><b>{plural(info.to_move, 'order')}</b> still at {from}. Use the button to move them and their stock.</>
                    : `Every pickup order is at ${venue}, and its stock moved with it.`}
              </p>
              {moveResult && (
                <PickupResult ok={moveResult.failed.length === 0}
                  title={moveResult.failed.length === 0
                    ? `All moved · ${plural(moveResult.moved.length, 'order')} now at ${venue}`
                    : `${moveResult.moved.length} of ${moveResult.moved.length + moveResult.failed.length} moved · `
                      + `${plural(moveResult.failed.length, 'order')} failed`}
                  failed={moveResult.failed}>
                  {moveResult.stock.map((t) => (
                    <div key={t.from}>
                      {t.error
                        ? <>Stock for {plural(t.units, 'unit')} didn't move from {t.from}: {t.error}. Fix the counts in Shopify.</>
                        : <>{plural(t.units, 'unit')} of stock moved from {t.from} to {venue}.</>}
                    </div>
                  ))}
                  {moveResult.failed.length > 0 && (
                    <div className="gs-sub">You can still mark everything ready. Orders that didn't move stay where they are.</div>
                  )}
                </PickupResult>
              )}
            </SubStep>

            <SubStep letter="B" title="Mark all ready for pickup" status={bAuto ? bAuto.status : bStatus} rule={bRule}
              when={bAuto ? bAuto.when : bWhen}
              action={armed ? (
                <button type="button" className="gs-btn danger" onClick={() => setAborted(true)}
                  disabled={!!busy || loading}>
                  {busy === 'auto' ? 'Aborting…' : 'Abort automation'}
                </button>
              ) : (
                <div className="gs-pick-acts">
                  <button type="button" className="gs-btn primary" onClick={markAll}
                    disabled={!!busy || loading || !info.can_mark || !info.mark_unlocked || info.to_mark === 0}>
                    {busy === 'mark' ? 'Marking…' : 'Mark all ready for pickup'}
                  </button>
                  {ra && ra.status === 'aborted' && ra.at && !kickoffPassed && (
                    <button type="button" className="gs-btn link" onClick={() => setAborted(false)} disabled={!!busy || loading}>
                      {busy === 'auto' ? 'Turning back on…' : 'Turn automation back on'}
                    </button>
                  )}
                </div>
              )}>
              <p>
                {bAuto && bAuto.text && (info.mark_unlocked || armed) ? bAuto.text
                  : !info.mark_unlocked
                    ? (!off ? (armed
                      ? `Runs on its own at ${fmtDT(ra.at)}, 2 hours before kickoff, after the store turns off and A moves the orders. Abort to mark them yourself.`
                      : `Unlocks when the store turns off, right after A. Press it once the stock is at ${venue}.`)
                      : 'Unlocks after A has run.')
                    : info.to_mark > 0
                      ? <><b>{info.to_mark} to mark ready.</b> Press once the stock is at {venue}. Shopify emails each customer.</>
                      : bDone ? 'Every order is marked ready.' : 'Nothing to mark.'}
              </p>
              {result && (
                <PickupResult ok={result.failed.length === 0}
                  title={result.failed.length === 0
                    ? (result.marked.length + result.already_ready.length === 0
                      ? 'Nothing to mark.'
                      : `All successful · ${plural(result.marked.length, 'order')} marked ready for pickup`
                        + (result.already_ready.length > 0 ? ` · ${result.already_ready.length} were already ready in Shopify` : ''))
                    : `${result.marked.length} of ${result.marked.length + result.already_ready.length + result.failed.length} marked`
                      + ` · ${plural(result.failed.length, 'order')} failed`}
                  failed={result.failed}>
                  {result.failed.length > 0 && (
                    <div className="gs-sub">Failed orders stay on the list. Fix them in Shopify or press the button again to retry them.</div>
                  )}
                </PickupResult>
              )}
            </SubStep>
            {!info.can_mark && <div className="gs-sub">Moving and marking only run on the live site.</div>}
          </div>
        )}
      </div>
    </li>
  );
}

// --- Main page -----------------------------------------------------------------------

export default function BopisScheduler({ team }) {
  const { nameOf } = useAuth();
  const api = team.api;
  const [overview, setOverview] = useState(null);
  const [state, setState] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [history, setHistory] = useState([]);
  const [stepLabels, setStepLabels] = useState(null); // { open: [...], close: [...] }
  const [activity, setActivity] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapErr, setSnapErr] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapAt, setSnapAt] = useState(null);
  const [run, setRun] = useState(null); // { action, steps, complete, error }
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null); // 'open' | 'close'
  const [editErr, setEditErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  // Page-only fonts from the design.
  useEffect(() => {
    if (document.querySelector(`link[href="${team.fontsHref}"]`)) return;
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = team.fontsHref;
    document.head.appendChild(l);
  }, [team.fontsHref]);

  const refreshAll = useCallback(async () => {
    const results = await Promise.allSettled([
      getJSON(`${api}/scheduler`),
      getJSON(`${api}/status`),
      getJSON(`${api}/schedule`),
      getJSON(`${api}/history?limit=20`),
      getJSON(`${api}/activity?limit=30`),
    ]);
    const [ov, st, sc, hi, ac] = results;
    if (ov.status === 'fulfilled') setOverview(ov.value);
    if (st.status === 'fulfilled') { setState(st.value.state); setStepLabels(st.value.steps || null); }
    if (sc.status === 'fulfilled') setSchedule(sc.value.home_games || []);
    if (hi.status === 'fulfilled') setHistory(hi.value.rows || []);
    if (ac.status === 'fulfilled') setActivity(ac.value.rows || []);
    const failed = results.find((r) => r.status === 'rejected');
    setLoadErr(failed ? failed.reason.message : null);
  }, [api]);

  const refreshVerify = useCallback(async () => {
    setSnapLoading(true); setSnapErr(null);
    try {
      const r = await getJSON(`${api}/verify`);
      if (!r.ok) throw new Error(r.error || 'verify failed');
      setSnapshot(r.snapshot);
      setSnapAt(new Date().toISOString());
    } catch (e) { setSnapErr(e.message); }
    finally { setSnapLoading(false); }
  }, [api]);

  useEffect(() => {
    refreshAll();
    const id = setInterval(refreshAll, POLL_MS);
    return () => clearInterval(id);
  }, [refreshAll]);

  const running = !!run && !run.complete;
  const today = todayET();
  const windows = useMemo(() => overview?.windows || [], [overview]);
  const next = windows.find((w) => w.game_date >= today) || null;
  const nextGame = next && schedule.find((g) => g.game_date === next.game_date && g.opponent === next.opponent);
  const kickIso = kickoffISO(nextGame);
  const paused = !!overview?.paused;
  const maxAttempts = overview?.max_attempts || 3;
  const isOpen = !!state?.is_open;
  const failures = (overview?.alerts || []).filter((a) => a.status === 'failed');
  const retrying = (overview?.alerts || []).filter((a) => a.status === 'retrying');
  const nextTurnOn = windows.find((w) => w.open_status === 'pending');

  const runAction = useCallback(async (kind) => {
    if (kind === 'open' && !next) return;
    const verb = kind === 'open' ? 'on' : 'off';
    const msg = kind === 'open'
      ? `Turn the stadium pickup store ON now for vs ${next.opponent} (${fmtDate(`${next.game_date}T12:00:00`)})?`
      : 'Turn the stadium pickup store OFF now?';
    if (!window.confirm(msg)) return;
    setActionErr(null);
    setRun({ action: kind, steps: [], complete: null, error: null });
    try {
      const body = kind === 'open'
        ? { game_date: next.game_date, opponent: next.opponent, open_at: next.open_at, close_at: next.close_at, dry_run: false }
        : { dry_run: false };
      for await (const ev of streamSSE(`${api}/${kind}`, body)) {
        if (ev.event === 'run-start') {
          setRun((r) => ({ ...r, steps: ev.data.steps.map((s) => ({ ...s, status: 'pending' })) }));
        } else if (ev.event === 'step-start') {
          setRun((r) => ({ ...r, steps: r.steps.map((s, i) => (i + 1 === ev.data.seq ? { ...s, status: 'running' } : s)) }));
        } else if (ev.event === 'step-result') {
          setRun((r) => ({
            ...r,
            steps: r.steps.map((s, i) => (i + 1 === ev.data.seq ? {
              ...s, status: ev.data.ok ? 'ok' : 'fail', detail: ev.data.detail, error: ev.data.error,
              shopify_admin_url: ev.data.shopify_admin_url,
            } : s)),
          }));
        } else if (ev.event === 'complete') {
          setRun((r) => ({
            ...r,
            steps: r.steps.map((s) => (s.status === 'pending' ? { ...s, status: 'skipped' } : s)),
            complete: ev.data,
          }));
          if (!ev.data.ok) setActionErr(`Turn-${verb} failed at ${ev.data.failed_step}. The store may be only partly ${verb}.`);
        }
      }
    } catch (e) {
      setRun((r) => ({ ...(r || { action: kind, steps: [] }), error: e.message, complete: { ok: false } }));
      setActionErr(`Turn-${verb} failed: ${e.message}`);
    } finally {
      refreshAll();
      if (snapshot) refreshVerify();
    }
  }, [api, next, refreshAll, refreshVerify, snapshot]);

  const resync = useCallback(async () => {
    setSyncing(true); setActionErr(null);
    try {
      const r = await sendJSON('POST', `${api}/schedule/refresh`, {});
      if (!r.ok) throw new Error(r.error || 'refresh failed');
    } catch (e) { setActionErr(`Schedule sync failed: ${e.message}`); }
    finally { setSyncing(false); refreshAll(); }
  }, [api, refreshAll]);

  const togglePause = useCallback(async () => {
    setBusy(true); setActionErr(null);
    try { await sendJSON('POST', `${api}/scheduler/pause`, { paused: !paused }); }
    catch (e) { setActionErr(e.message); }
    finally { setBusy(false); refreshAll(); }
  }, [api, paused, refreshAll]);

  // The time saved, but the store is open and its countdown couldn't be moved.
  const showCountdownError = useCallback((res) => {
    const err = res && res.window && res.window.countdown_error;
    if (err) setActionErr(`Time saved, but the store page countdown wasn't updated: ${err}`);
  }, []);

  const saveTime = useCallback(async (edge, draft) => {
    const iso = etToISO(draft.date, draft.time);
    if (!iso) { setEditErr('Pick a date and a time.'); return; }
    setBusy(true); setEditErr(null);
    try {
      const res = await sendJSON('PATCH', `${api}/windows/${next.id}`, { edge, at: iso });
      setEditing(null);
      showCountdownError(res);
      refreshAll();
    } catch (e) { setEditErr(e.message); }
    finally { setBusy(false); }
  }, [api, next, refreshAll, showCountdownError]);

  const resetTime = useCallback(async (edge) => {
    setBusy(true); setActionErr(null);
    try { showCountdownError(await sendJSON('PATCH', `${api}/windows/${next.id}`, { edge, at: null })); }
    catch (e) { setActionErr(e.message); }
    finally { setBusy(false); refreshAll(); }
  }, [api, next, refreshAll, showCountdownError]);

  // Live run for an action, else its last recorded run.
  const runViewFor = (action) => {
    if (run && run.action === action && run.steps.length) {
      return {
        live: !run.complete,
        heading: run.complete ? (run.complete.ok ? 'This run · all steps done' : 'This run · stopped') : 'Running…',
        steps: run.steps,
      };
    }
    const last = history.find((h) => h.action === action && !h.dry_run);
    if (!last) return null;
    const who = last.actor === 'auto' ? 'automatic' : nameOf(last.actor);
    return { live: false, heading: `Last run · ${who} · ${fmtDT(last.executed_at)}`, steps: stepsFromHistory(last) };
  };

  // The next scheduled turn-off, listed unchecked until it runs.
  const closeUpcoming = next && next.close_status === 'pending' && stepLabels?.close?.length
    ? {
      heading: `Upcoming run · ${fmtDT(next.close_at)}${paused ? ' (paused)' : ''} · not run yet`,
      steps: stepLabels.close.map((s) => ({ name: s.name, label: s.label, status: 'upcoming' })),
    }
    : null;

  const openStatus = edgeStatus(next, 'open', paused, maxAttempts);
  const closeStatus = edgeStatus(next, 'close', paused, maxAttempts);
  const failedClose = failures.some((f) => f.edge === 'close');
  const failedOpen = failures.some((f) => f.edge === 'open');
  const lastSyncRow = activity.find((a) => /^Schedule synced/.test(a.message));

  let bannerSub;
  if (!state && !overview) bannerSub = 'Loading…';
  else if (isOpen) bannerSub = `Customers can order for stadium pickup · turns off ${next && next.close_status === 'pending' ? fmtDT(next.close_at) : '—'}`;
  else bannerSub = `No stadium pickup · next turn-on ${nextTurnOn ? fmtDT(nextTurnOn.open_at) : 'not scheduled'}`;
  if (paused) bannerSub += ' · automation paused';

  return (
    <div className={`gs-page ${team.themeClass}`.trim()}>
      <div role="status" aria-live="polite" className={`gs-banner ${isOpen ? 'on' : 'off'}`}>
        <div className="gs-banner-l">
          <div className="gs-dot" />
          <div>
            <div className="gs-eyebrow">{team.eyebrow}</div>
            <div className="gs-banner-title">{isOpen ? 'Pickup store live' : 'Pickup store off'}</div>
            <div className="gs-banner-sub">{bannerSub}</div>
          </div>
        </div>
        <button type="button" className="gs-banner-btn" disabled={running || (!isOpen && !next)}
          onClick={() => runAction(isOpen ? 'close' : 'open')}>
          {isOpen ? 'Turn off now' : 'Turn on now'}
        </button>
      </div>

      {failures.map((f) => (
        <div key={`${f.window_id}-${f.edge}`} role="alert" className="gs-alert">
          <div>
            <div className="gs-alert-t">Automatic turn-{EDGE_WORD[f.edge]} failed after {f.attempts} tries</div>
            <div className="gs-alert-d">
              vs {f.opponent} · last try {fmtDT(f.last_try_at)} · {f.error || 'unknown error'}.
              {' '}The store may be only partly {EDGE_WORD[f.edge]}. Check it in Shopify, then retry.
            </div>
          </div>
          <button type="button" className="gs-btn danger" disabled={running} onClick={() => runAction(f.edge)}>
            Retry turn-{EDGE_WORD[f.edge]} now
          </button>
        </div>
      ))}
      {retrying.map((f) => (
        <div key={`${f.window_id}-${f.edge}`} role="status" className="gs-alert warn">
          <div className="gs-alert-d">
            Automatic turn-{EDGE_WORD[f.edge]} for vs {f.opponent} failed ({f.error || 'unknown error'}).
            {' '}Trying again within 15 min (try {f.attempts + 1} of {maxAttempts}).
          </div>
        </div>
      ))}
      {(actionErr || loadErr) && (
        <div role="alert" className="gs-alert">
          <div className="gs-alert-d">{actionErr || `Couldn’t load everything: ${loadErr}`}</div>
        </div>
      )}

      <div className="gs-grid">
        <section className="gs-main">
          <div className="gs-head">
            <div className="gs-head-l">
              {next && (
                <div className="gs-crests">
                  <Crest src={team.homeCrest} alt={`${team.homeName} crest`} />
                  <Crest src={badgeForOpponent(next.opponent)} alt={`${next.opponent} crest`} />
                </div>
              )}
              <div>
                <div className="gs-kicker">Next home match</div>
                <h2 className="gs-match">{next ? `vs ${next.opponent}` : 'No upcoming home games'}</h2>
                {next && (
                  <div className="gs-sub lg">
                    {parseGameDate(next.game_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {' · '}{nextGame ? fmtKickoff(nextGame.kickoff_time) : 'kickoff TBA'}{nextGame?.kickoff_time ? ' kickoff' : ''}
                    {' · '}{team.stadium}
                  </div>
                )}
                {nextGame?.theme && <div className="gs-theme lg">{nextGame.theme}</div>}
              </div>
            </div>
            <div className="gs-auto">
              <div>
                <div className="strong">Automation</div>
                <div className="gs-sub">{paused ? 'Paused · nothing runs on its own' : 'On · checks every 15 min'}</div>
              </div>
              <button type="button" className={`gs-btn ${paused ? 'primary' : 'ghost'}`} aria-pressed={paused}
                disabled={busy || !overview} onClick={togglePause}>
                {paused ? 'Resume' : 'Pause'}
              </button>
            </div>
          </div>

          {next && (
            <div className="gs-window">
              <div className="gs-window-h">
                <h2 className="gs-h2">Store window</h2>
                <span className="gs-mono accent">{windowLength(next.open_at, next.close_at, kickIso)}</span>
              </div>
              <div className="gs-edges">
                {['open', 'close'].map((edge) => (
                  <EdgeBox key={edge} w={next} edge={edge} editing={editing === edge} busy={busy}
                    error={editing === edge ? editErr : null}
                    onEdit={() => { setEditing(edge); setEditErr(null); }}
                    onCancel={() => { setEditing(null); setEditErr(null); }}
                    onSave={(draft) => saveTime(edge, draft)}
                    onReset={() => resetTime(edge)} />
                ))}
              </div>
              <Timeline openIso={next.open_at} closeIso={next.close_at} kickIso={kickIso} />
            </div>
          )}

          <ol className="gs-steps">
            <StepCard n={1} title="Sync home schedule"
              desc="Pull the latest home fixtures so every game has a window. Runs daily at 6:00 AM."
              rule="Daily 6:00 AM"
              status={{ label: syncing ? 'Running' : 'Completed', tone: syncing ? 'sched' : 'done' }}
              when={lastSyncRow ? `Ran ${fmtDT(lastSyncRow.at)} · ${lastSyncRow.who}` : overview?.last_sync ? `Synced ${fmtDT(overview.last_sync)}` : '—'}
              runLabel={syncing ? 'Syncing…' : 'Re-sync now'} runDisabled={syncing} onRun={resync} />
            <StepCard n={2} title="Turn store ON"
              desc="Rename the stadium location for the game, turn on online fulfillment and pickup, add the Stadium Collection to the menu and start the countdown."
              rule="Mon 10:00 AM, game week" status={openStatus} when={openStatus.when} custom={next?.open_custom}
              runLabel={failedOpen ? 'Retry now' : 'Turn on now'}
              runDisabled={running || !next || (isOpen && !failedOpen)} onRun={() => runAction('open')}
              runView={runViewFor('open')} />
            <StepCard n={3} title="Turn store OFF"
              desc={`Undo all of that. If Shopify fails, it retries on the next two sweeps (15 min apart), then alerts.`}
              rule="Fri noon, or day before" status={closeStatus} when={closeStatus.when} custom={next?.close_custom}
              runLabel={failedClose ? 'Retry now' : 'Turn off now'}
              runDisabled={running || (!isOpen && !failedClose)} onRun={() => runAction('close')}
              runView={runViewFor('close')} upcoming={closeUpcoming} compactLast />
            {team.pickupReady && next && <PickupOrders api={api} w={next} n="04" venue={team.pickupVenue} storageKey={team.step4Key} />}
          </ol>

          <LiveState snapshot={snapshot} loading={snapLoading} error={snapErr} checkedAt={snapAt} onRefresh={refreshVerify} metafieldNs={team.metafieldNs} />
        </section>

        <aside className="gs-side">
          <div className="gs-panel">
            <div className="gs-panel-h">
              <h2 className="gs-h2">Home games</h2>
              {overview?.last_sync && <span className="gs-sub">Synced {fmtStamp(overview.last_sync)}</span>}
            </div>
            <div className="gs-sub">Every home game gets a window automatically.</div>
            {windows.filter((w) => w.game_date >= today).length === 0 && (
              <div className="gs-sub">No upcoming home games. Try Re-sync now.</div>
            )}
            {windows.filter((w) => w.game_date >= today).map((w) => {
              const d = parseGameDate(w.game_date);
              const g = schedule.find((x) => x.game_date === w.game_date && x.opponent === w.opponent);
              const isNext = next && w.id === next.id;
              return (
                <div key={w.id} className={`gs-fix${isNext ? ' next' : ''}`}>
                  <div className="gs-fix-top">
                    <div className="gs-cal">
                      <span>{d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</span>
                      <b>{d.getDate()}</b>
                    </div>
                    <div className="gs-fix-body">
                      <div className="strong">vs {w.opponent}</div>
                      <div className="gs-sub">
                        {d.toLocaleDateString('en-US', { weekday: 'short' })} · {g ? fmtKickoff(g.kickoff_time) : 'kickoff TBA'}
                      </div>
                      {g?.theme && <div className="gs-theme">{g.theme}</div>}
                    </div>
                    <Pill tone={isNext ? 'done' : 'sched'}>{isNext ? (isOpen ? 'Live' : 'Next') : 'Scheduled'}</Pill>
                  </div>
                  <div className="gs-fix-times">
                    <div className="on">
                      <span>TURNS ON{w.open_custom ? ' · CUSTOM' : ''}</span>
                      <b>{fmtDT(w.open_at)}</b>
                    </div>
                    <div className="off">
                      <span>TURNS OFF{w.close_custom ? ' · CUSTOM' : ''}</span>
                      <b>{fmtDT(w.close_at)}</b>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="gs-panel gs-log">
            <h2 className="gs-h2">Activity log</h2>
            {activity.length === 0 && <div className="gs-sub">Nothing yet.</div>}
            <ul>
              {activity.map((e, i) => (
                <li key={i}>
                  <span className={`gs-who ${e.who}`} title={e.actor ? nameOf(e.actor) : e.who}>
                    {e.actor ? nameOf(e.actor) : e.who}
                  </span>
                  <div>
                    <div>{e.message}</div>
                    <div className="gs-mono dim">{fmtStamp(e.at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
